import React, { useState } from 'react';
import {
  FaUserTie, FaEnvelope, FaPhone, FaBuilding, FaTimes,
  FaCheck, FaLink, FaCopy, FaPaperPlane
} from 'react-icons/fa';
import { useAuth, axiosInstance as axios } from '../../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const PROFESSIONAL_TYPES = [
  { value: 'financial_advisor', label: 'Financial Advisor', icon: '💼' },
  { value: 'estate_attorney', label: 'Estate Attorney', icon: '⚖️' },
  { value: 'cpa_tax', label: 'CPA & Tax Expert', icon: '📊' },
  { value: 'real_estate', label: 'Real Estate Expert', icon: '🏠' },
  { value: 'insurance', label: 'Insurance Specialist', icon: '🛡️' },
  { value: 'family_therapist', label: 'Family Therapist', icon: '❤️' }
];

export default function InviteProfessionalModal({ familyId, onClose, onSuccess }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    professional_type: '',
    company: '',
    specialties: [],
    relationship: '',
    recommendation_note: ''
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invitationResult, setInvitationResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSpecialty = () => {
    if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, newSpecialty.trim()]
      }));
      setNewSpecialty('');
    }
  };

  const removeSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!formData.name || !formData.email || !formData.professional_type) {
      setError('Please fill in all required fields');
      setSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/directory/invite?family_id=${familyId}`,
        formData,
        { withCredentials: true }
      );
      setInvitationResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const getFullSignupLink = () => {
    if (!invitationResult) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/professionals/signup?token=${invitationResult.invitation_token}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getFullSignupLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getFullSignupLink();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Success State
  if (invitationResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="invitation-success-modal">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheck className="text-green-600 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Invitation Created!</h3>
            <p className="text-gray-600 mb-6">
              Share this link with <strong>{formData.name}</strong> so they can complete their profile.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 mb-2">Invitation Link:</p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={getFullSignupLink()}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 truncate"
                  data-testid="invitation-link-input"
                />
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  data-testid="copy-link-btn"
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>What happens next?</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• {formData.name} clicks the link to complete their profile</li>
                <li>• Once complete, you can approve them as a trusted professional</li>
                <li>• They'll appear in the directory visible to all families</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  onSuccess && onSuccess(invitationResult);
                  onClose();
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                data-testid="done-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form State
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="invite-professional-modal">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Invite a Professional</h3>
              <p className="text-gray-600 text-sm mt-1">
                Know a great advisor? Invite them to join our directory.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              data-testid="close-modal-btn"
            >
              <FaTimes />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Professional Type */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Type of Professional *</label>
              <div className="grid grid-cols-2 gap-2">
                {PROFESSIONAL_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, professional_type: type.value }))}
                    className={`p-2 rounded-lg border text-left text-sm transition ${
                      formData.professional_type === type.value
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`type-btn-${type.value}`}
                  >
                    <span className="mr-1">{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Professional's Name *</label>
              <div className="relative">
                <FaUserTie className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="Dr. Jane Smith"
                  required
                  data-testid="prof-name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Email Address *</label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="jane@example.com"
                  required
                  data-testid="prof-email-input"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Phone Number</label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="(555) 123-4567"
                  data-testid="prof-phone-input"
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Company/Practice</label>
              <div className="relative">
                <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="Smith Financial Group"
                  data-testid="prof-company-input"
                />
              </div>
            </div>

            {/* Specialties */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Their Specialties</label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 text-sm"
                  placeholder="e.g., Estate Planning"
                />
                <button
                  type="button"
                  onClick={addSpecialty}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.specialties.map((specialty, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                  >
                    {specialty}
                    <button
                      type="button"
                      onClick={() => removeSpecialty(specialty)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Relationship */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Your Relationship</label>
              <input
                type="text"
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                placeholder="e.g., Our family accountant for 10 years"
                data-testid="relationship-input"
              />
            </div>

            {/* Recommendation Note */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Why do you recommend them?</label>
              <textarea
                name="recommendation_note"
                value={formData.recommendation_note}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
                placeholder="They helped us navigate our estate planning beautifully..."
                data-testid="recommendation-input"
              />
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>How it works:</strong> We'll generate an invitation link for you to share. 
                Once they complete their profile, you'll be able to approve them before they appear in the directory.
              </p>
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center justify-center"
                data-testid="create-invitation-btn"
              >
                {submitting ? (
                  'Creating...'
                ) : (
                  <>
                    <FaLink className="mr-2" />
                    Create Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
